import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  projectReferencesService,
  type ProjectReferencesListResult,
  type ProjectReferenceWarning,
} from '@/shared/api/services/project-references.service';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type {
  ProjectReferenceCategory,
  ProjectReferenceAspect,
  ProjectReferenceSection,
} from '@/types/project-reference';
import {
  useClassificationWatchStore,
  withStalledReferences,
} from '@/features/project-reference/lib/classification-watch';
import { useClassificationWatch } from './use-classification-watch';

// Hook for fetching all project references
// `section` (brief | reference | undefined=all) filters by intake bucket.
// `enablePolling` runs the classification fallback poll for this list (the
// socket answers most cases); every list shows given-up references as failed.
export function useProjectReferences(
  projectId: string,
  options?: {
    enabled?: boolean;
    enablePolling?: boolean;
    section?: ProjectReferenceSection;
  }
) {
  const section = options?.section;
  const enabled = !!projectId && options?.enabled !== false;
  const stalled = useClassificationWatchStore((state) => state.stalled);
  const select = useCallback(
    (result: ProjectReferencesListResult) =>
      withStalledReferences(result.references, stalled),
    [stalled]
  );

  useClassificationWatch(projectId, section, {
    enabled,
    polling: options?.enablePolling === true,
  });

  return useQuery({
    queryKey: cacheKeys.references.list(projectId, section),
    queryFn: () =>
      projectReferencesService.listProjectReferences(projectId, section),
    select,
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: true,
  });
}

// Hook for fetching warnings from the references endpoint
// Shares cache with useProjectReferences
export function useProjectReferenceWarnings(
  projectId: string,
  section?: ProjectReferenceSection
): ProjectReferenceWarning[] {
  const { data } = useQuery({
    queryKey: cacheKeys.references.list(projectId, section),
    queryFn: () =>
      projectReferencesService.listProjectReferences(projectId, section),
    select: (result) => result.warnings,
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
  return data ?? [];
}

// Hook for fetching references by category
export function useProjectReferencesByCategory(
  projectId: string,
  category: ProjectReferenceCategory,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: cacheKeys.references.byCategory(projectId, category),
    queryFn: () =>
      projectReferencesService.getProjectReferencesByCategory(
        projectId,
        category
      ),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// Hook for fetching a single project reference
export function useProjectReference(
  projectId: string,
  referenceId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: cacheKeys.references.detail(projectId, referenceId),
    queryFn: () =>
      projectReferencesService.getProjectReference(projectId, referenceId),
    enabled: !!projectId && !!referenceId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for creating a project reference
export function useCreateProjectReference(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'create'],
    mutationFn: (data: {
      md5: string;
      fileName?: string;
      contentType: string;
      fileSize: number;
      pageCount?: number;
    }) => projectReferencesService.createProjectReference(projectId, data),
    onSuccess: () => {
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
    },
  });
}

// Hook for confirming upload
export function useConfirmProjectReferenceUpload(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'confirm-upload'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: (referenceId: string) =>
      projectReferencesService.confirmUpload(projectId, referenceId),
    onSuccess: (reference) => {
      // Update the cache with the confirmed reference
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
    },
  });
}

// Hook for updating reference category
export function useUpdateProjectReferenceCategory(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'update-category'],
    meta: { lockId: (vars: unknown) => (vars as { referenceId: string }).referenceId },
    mutationFn: ({
      referenceId,
      category,
    }: {
      referenceId: string;
      category: ProjectReferenceCategory;
    }) =>
      projectReferencesService.updateCategory(projectId, referenceId, category),
    onSuccess: (reference) => {
      // Update cache
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
    },
  });
}

// Hook for updating reference attributes (category + description)
export function useUpdateProjectReference(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['references', 'update'],
    meta: { lockId: (vars: unknown) => (vars as { referenceId: string }).referenceId },
    mutationFn: ({
      referenceId,
      category,
      aspects,
      description,
      section,
    }: {
      referenceId: string;
      category?: ProjectReferenceCategory;
      aspects?: ProjectReferenceAspect[];
      description?: string | null;
      section?: ProjectReferenceSection;
    }) => {
      const attrs: {
        category?: ProjectReferenceCategory;
        aspects?: ProjectReferenceAspect[];
        description?: string | null;
        section?: ProjectReferenceSection;
      } = {};
      if (category !== undefined) attrs.category = category;
      if (aspects !== undefined) attrs.aspects = aspects;
      if (description !== undefined) attrs.description = description;
      if (section !== undefined) attrs.section = section;
      return projectReferencesService.updateReference(
        projectId,
        referenceId,
        attrs
      );
    },
    onSuccess: (reference) => {
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
    },
  });
}

// Hook for moving a reference between intake sections (brief <-> reference).
// Fixes a wrong-section upload: flips `data.attributes.section`, then sweeps
// the section-trailing list prefix so the item leaves one section and appears
// in the other on the next refetch.
export function useMoveProjectReferenceSection(projectId: string) {
  const queryClient = useQueryClient();
  const t = useTranslations('projectReference');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['references', 'move-section'],
    meta: {
      lockId: (vars: unknown) => (vars as { referenceId: string }).referenceId,
    },
    mutationFn: ({
      referenceId,
      section,
    }: {
      referenceId: string;
      section: ProjectReferenceSection;
    }) =>
      projectReferencesService.updateSection(projectId, referenceId, section),
    onSuccess: (reference) => {
      queryClient.setQueryData(
        cacheKeys.references.detail(projectId, reference.id),
        reference
      );
      // Sweep both the brief and reference section lists so the moved item
      // disappears from its old section and shows up in the new one.
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });
      toast.success(
        reference.attributes.section === 'brief'
          ? t('detail.movedToBrief')
          : t('detail.movedToReferences')
      );
    },
    // Surface the server's verbatim rejection (e.g. cap reached) as-is, mirroring
    // the convert (view↔doc) path. Without this the move 422 was swallowed.
    onError: (error) => {
      toast.error(
        error instanceof ApiClientError
          ? error.getFirstError()
          : tCommon('errors.generic')
      );
    },
  });
}

// Hook for deleting a project reference with undo support
export function useDeleteProjectReference(projectId: string) {
  const queryClient = useQueryClient();
  const t = useTranslations('projectReference');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['references', 'delete'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: (referenceId: string) =>
      projectReferencesService.deleteProjectReference(projectId, referenceId),
    onSuccess: (_, referenceId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: cacheKeys.references.detail(projectId, referenceId),
      });
      // Invalidate list to refetch
      queryClient.invalidateQueries({
        queryKey: cacheKeys.references.listsByProject(projectId),
      });

      // Show undo toast
      toast.undoable(t('referenceDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await projectReferencesService.restoreProjectReference(
                projectId,
                referenceId
              );
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t('failedToRestoreReference')
              );
            }
            queryClient.invalidateQueries({
              queryKey: cacheKeys.references.listsByProject(projectId),
            });
          },
        },
      });
    },
  });
}

// Picker variant of the references hook. Surfaces references regardless of
// classification status — completed, failed, AND in-flight (pending/processing)
// — so a freshly uploaded reference stays visible in the render picker grid
// (with a processing overlay) instead of vanishing for ~30s until server
// classification completes. Mirrors Apple `ProjectReferenceGridView` / Android
// `ReferencePickerPanel`, which show all statuses. Enables polling as a
// WebSocket safety net so the card resolves even if the WS event is missed.
// (Keep `useClassifiedProjectReferences` completed-only for the Automatic-pool
// preview, which should only show refs that are actually usable.)
export function useSelectableProjectReferences(
  projectId: string,
  options?: { enabled?: boolean }
) {
  return useProjectReferences(projectId, {
    ...options,
    enablePolling: true,
  });
}

// Helper hook to get classified references by category
// Used in render settings selector - includes both user and system refs
export function useClassifiedProjectReferences(
  projectId: string,
  category?: ProjectReferenceCategory,
  options?: { enabled?: boolean }
) {
  const { data: references, ...rest } = useProjectReferences(
    projectId,
    options
  );

  const filteredReferences = references?.filter((ref) => {
    // Only completed classifications (ready to use)
    if (ref.attributes.classification_status !== 'completed') return false;
    // Filter by category if specified
    if (category && ref.attributes.category !== category) return false;
    return true;
  });

  return {
    data: filteredReferences,
    ...rest,
  };
}
